import type { SettingsStore } from "../settings.ts";
import { FrameSampler } from "./frames.ts";
import type { PerfLink } from "./link.ts";
import { writePerformance } from "./setting.ts";
import { perfView, type PerfView } from "./view.ts";

/**
 * The performance panel's store and clock (ADR-087 decisions 2, 3 and 6). While the panel shows, every drawn frame's
 * time goes into the frame ring, and twice a second the panel's content is rebuilt from the page's counters and the
 * current connection's `PerfLink`; a request for the server half goes once per the interval the gateway offered,
 * and a keepalive `ping` every 2 seconds times the round trip. While it is hidden nothing is recorded, sent or rebuilt.
 * The counts of what the page holds are shown only on a connection the gateway offered the server half to.
 */

/**
 * Milliseconds between the panel's updates.
 */
export const PERF_UPDATE_MS = 500;

/**
 * Milliseconds between the pings the panel sends while it shows.
 */
export const PERF_PING_MS = 2000;

/**
 * How long a report counts as fresh, in milliseconds; an older one hides the server half.
 */
export const PERF_FRESH_MS = 3000;

/**
 * What the page counts at an update: the world scene's game objects, the props loaded, and the other players,
 * creatures and things in the client view. Only the props are shown to every player; the rest are a staff viewer's,
 * as the bytes received are.
 */
export interface SceneCounts {
  readonly objects: number;
  readonly props: number;
  readonly players: number;
  readonly creatures: number;
  readonly things: number;
}

/**
 * Where the monitor reads: the current connection's link (null between connections), the scene's counts and the page
 * clock.
 */
export interface PerfSources {
  readonly link: () => PerfLink | null;
  readonly counts: () => SceneCounts;
  readonly now: () => number;
}

/**
 * What the panel draws: whether it shows, and its content once the first update after showing has run.
 */
export interface PerfSnapshot {
  readonly shown: boolean;
  readonly view: PerfView | null;
}

/**
 * Runs a callback on an interval.
 *
 * @param ms The interval.
 * @param run The callback.
 * @returns A function that stops it.
 */
function everyMs(ms: number, run: () => void): () => void {
  const timer = setInterval(run, ms);
  return () => clearInterval(timer);
}

/**
 * The monitor.
 */
export class PerfMonitor {
  private readonly frames = new FrameSampler();
  private readonly listeners = new Set<() => void>();
  private snapshot: PerfSnapshot = { shown: false, view: null };
  private stop: (() => void) | null = null;
  private link: PerfLink | null = null;
  private bytesAt = 0;
  private bytesSeen = 0;
  private requestedAt = Number.NEGATIVE_INFINITY;
  private pingedAt = Number.NEGATIVE_INFINITY;

  /**
   * @param sources What it reads.
   * @param options Whether the panel shows as the page loads, where the setting is kept (null for nowhere) and the
   *   interval timer, the page's `setInterval` by default.
   */
  public constructor(
    private readonly sources: PerfSources,
    private readonly options: { readonly shown: boolean; readonly store: SettingsStore | null; readonly every?: (ms: number, run: () => void) => () => void },
  ) {
    if (options.shown) {
      this.start(false);
    }
  }

  /**
   * Adds a listener told after each change. Works unbound, as React calls it.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the panel draws. Works unbound, as React calls it.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): PerfSnapshot => this.snapshot;

  /**
   * Whether the panel shows.
   *
   * @returns True while it shows.
   */
  public get shown(): boolean {
    return this.snapshot.shown;
  }

  /**
   * Shows or hides the panel and keeps the choice per viewer.
   *
   * @param shown Whether it shows.
   */
  public setShown(shown: boolean): void {
    if (shown === this.snapshot.shown) {
      return;
    }
    writePerformance(shown, this.options.store);
    if (shown) {
      this.start(true);
    } else {
      this.stop?.();
      this.stop = null;
      this.frames.clear();
      this.set({ shown: false, view: null });
    }
  }

  /**
   * Shows the panel when hidden and hides it when shown, as F9 does.
   */
  public toggle(): void {
    this.setShown(!this.snapshot.shown);
  }

  /**
   * Records one drawn frame's time while the panel shows.
   *
   * @param deltaMs Milliseconds since the frame before.
   */
  public frame(deltaMs: number): void {
    if (this.snapshot.shown) {
      this.frames.record(deltaMs);
    }
  }

  /**
   * One update: the bytes received since the last on the same connection, a request when the offered interval has
   * passed, a ping every `PERF_PING_MS`, and the content rebuilt with the last report while it is fresh, the staff
   * rows only when the current connection holds the gateway's offer.
   */
  public update(): void {
    if (!this.snapshot.shown) {
      return;
    }
    const now = this.sources.now();
    const link = this.sources.link();
    let bytesPerSecond: number | null = null;
    if (link !== this.link) {
      this.link = link;
      this.requestedAt = Number.NEGATIVE_INFINITY;
      this.pingedAt = Number.NEGATIVE_INFINITY;
    } else if (link !== null && now > this.bytesAt) {
      bytesPerSecond = ((link.bytes - this.bytesSeen) * 1000) / (now - this.bytesAt);
    }
    this.bytesAt = now;
    this.bytesSeen = link?.bytes ?? 0;
    if (link !== null) {
      const interval = link.offeredMs;
      if (interval !== null && now - this.requestedAt >= interval && link.request()) {
        this.requestedAt = now;
      }
      if (now - this.pingedAt >= PERF_PING_MS && link.ping(now)) {
        this.pingedAt = now;
      }
    }
    const last = link?.report ?? null;
    const report = last !== null && now - last.at <= PERF_FRESH_MS ? last.report : null;
    const counts = this.sources.counts();
    this.set({ shown: true, view: perfView({ frames: this.frames.summary(), ...counts, bytesPerSecond, pingMs: link?.rttMs ?? null }, report, link !== null && link.offeredMs !== null) });
  }

  /**
   * Starts the updates and shows the panel, its content from the first update on: at once when the viewer shows it,
   * and at the first interval as the page loads, before the page has made what the sources read.
   *
   * @param now Whether the first update runs now.
   */
  private start(now: boolean): void {
    this.link = null;
    this.set({ shown: true, view: null });
    this.stop = (this.options.every ?? everyMs)(PERF_UPDATE_MS, () => this.update());
    if (now) {
      this.update();
    }
  }

  /**
   * Replaces the snapshot and tells the listeners.
   *
   * @param next The snapshot.
   */
  private set(next: PerfSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
